import { useEffect, useState } from "react";
import { api } from "../lib/api";
import Reveal from "../components/Reveal";
import TeamPhoto3D from "../components/TeamPhoto3D";
import TypographyOverride from "../components/TypographyOverride";
import StructuredData, { faqPageLD, breadcrumbLD } from "../components/StructuredData";

export default function About({ settings }) {
  const [about, setAbout] = useState(null);
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    api.get("/about").then(({ data }) => setAbout(data));
    api.get("/faqs").then(({ data }) => setFaqs(data || [])).catch(() => setFaqs([]));
  }, []);

  if (!about) {
    return (
      <main className="min-h-[80vh] flex items-center justify-center">
        <p className="overline text-[#8A8A93]">Loading…</p>
      </main>
    );
  }

  // Resolve CTA href: explicit href > whatsapp > contact_email mailto
  const ctaHref =
    about.cta_button_href?.trim() ||
    (settings?.whatsapp_number
      ? `https://wa.me/${settings.whatsapp_number}?text=${encodeURIComponent(
          settings.whatsapp_message || "Hi, I'd like to discuss a project."
        )}`
      : settings?.contact_email
        ? `mailto:${settings.contact_email}`
        : "/work");

  // Opening headline: split last word as gold italic if `opening_accent` empty
  const heading = (about.opening_line || "").trim();
  const accent = (about.opening_accent || "").trim();
  const headingParts = (() => {
    if (!heading) return null;
    if (accent && heading.toLowerCase().includes(accent.toLowerCase())) {
      const idx = heading.toLowerCase().lastIndexOf(accent.toLowerCase());
      return {
        before: heading.slice(0, idx),
        accent: heading.slice(idx, idx + accent.length),
        after: heading.slice(idx + accent.length),
      };
    }
    // fallback — last word
    const words = heading.split(/\s+/);
    if (words.length < 2) return { before: "", accent: heading, after: "" };
    const last = words[words.length - 1];
    return {
      before: words.slice(0, -1).join(" ") + " ",
      accent: last.replace(/\.$/, ""),
      after: last.endsWith(".") ? "." : "",
    };
  })();

  const disciplines = (about.disciplines || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  const visibleTeam = (about.team || []).filter((m) => m.visible !== false && (m.name || m.role));

  return (
    <main data-testid="about-page" className="about-page bg-[#0A0A0C]">
      <TypographyOverride pageId="about" scopeSelector=".about-page" prefix="ab" />
      {/* JSON-LD FAQ + breadcrumb — feeds ChatGPT / Perplexity / Google AI */}
      <StructuredData
        id="about-faq"
        data={faqPageLD(faqs.map((f) => ({ q: f.question, a: f.answer })))}
      />
      <StructuredData
        id="about-crumb"
        data={breadcrumbLD([
          { name: "Home", item: "/" },
          { name: "About", item: "/about" },
        ])}
      />
      {/* Section 1 — Opening */}
      {about.opening_visible && headingParts && (
        <section data-testid="about-opening" className="px-6 md:px-14 lg:px-24 pt-32 md:pt-44 pb-24 md:pb-36 border-b border-white/5">
          <Reveal>
            <p className="ab-section-label overline mb-10 md:mb-16">{about.opening_overline || "About"}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="ab-opening font-display text-5xl sm:text-6xl md:text-7xl lg:text-[7rem] leading-[0.95] tracking-[-0.04em] max-w-[18ch]">
              {headingParts.before}
              <span className="font-serif-accent text-[#C9A050]">{headingParts.accent}</span>
              {headingParts.after}
            </h1>
          </Reveal>
        </section>
      )}

      {/* Section 2 — Intro paragraph */}
      {about.intro_visible && about.intro_body && (
        <section data-testid="about-intro" className="px-6 md:px-14 lg:px-24 py-24 md:py-36 border-b border-white/5">
          <div className="grid grid-cols-12 gap-6 md:gap-12">
            <div className="col-span-12 md:col-span-3">
              <Reveal>
                <p className="ab-section-label overline text-[#8A8A93]">{about.intro_label || "Who we are"}</p>
              </Reveal>
            </div>
            <div className="col-span-12 md:col-span-8 md:col-start-5">
              <Reveal delay={0.1}>
                <p className="ab-intro-body text-[#E4E4E7] text-2xl md:text-3xl lg:text-[2.25rem] leading-[1.45] font-light tracking-[-0.01em] whitespace-pre-line">
                  {about.intro_body}
                </p>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* Section 3 — Disciplines */}
      {about.disciplines_visible && disciplines.length > 0 && (
        <section data-testid="about-disciplines" className="px-6 md:px-14 lg:px-24 py-24 md:py-36 border-b border-white/5">
          <div className="grid grid-cols-12 gap-6 md:gap-12">
            <div className="col-span-12 md:col-span-3">
              <Reveal>
                <p className="ab-section-label overline text-[#8A8A93]">{about.disciplines_label || "What we do"}</p>
              </Reveal>
            </div>
            <div className="col-span-12 md:col-span-8 md:col-start-5">
              <ul className="space-y-2 md:space-y-3">
                {disciplines.map((d, i) => (
                  <Reveal key={i} delay={i * 0.05}>
                    <li className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-[-0.02em]">
                      {d}
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* Section 4 — Approach */}
      {about.approach_visible && about.approach_body && (
        <section data-testid="about-approach" className="px-6 md:px-14 lg:px-24 py-24 md:py-36 border-b border-white/5">
          <div className="grid grid-cols-12 gap-6 md:gap-12">
            <div className="col-span-12 md:col-span-3">
              <Reveal>
                <p className="ab-section-label overline text-[#8A8A93]">{about.approach_label || "Approach"}</p>
              </Reveal>
            </div>
            <div className="col-span-12 md:col-span-8 md:col-start-5">
              <Reveal delay={0.1}>
                <p className="font-serif-accent text-[#E4E4E7] text-2xl md:text-3xl lg:text-4xl leading-[1.4] font-light max-w-3xl whitespace-pre-line">
                  {about.approach_body}
                </p>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* Section 5 — Team */}
      {about.team_visible && (about.team_intro || visibleTeam.length > 0) && (
        <section data-testid="about-team" className="px-6 md:px-14 lg:px-24 py-24 md:py-36 border-b border-white/5">
          <div className="grid grid-cols-12 gap-6 md:gap-12 mb-20 md:mb-32">
            <div className="col-span-12 md:col-span-3">
              <Reveal>
                <p className="ab-section-label overline text-[#8A8A93]">{about.team_label || "The team"}</p>
              </Reveal>
            </div>
            {about.team_intro && (
              <div className="col-span-12 md:col-span-8 md:col-start-5">
                <Reveal delay={0.1}>
                  <p className="ab-body text-[#A1A1AA] text-lg md:text-xl leading-relaxed max-w-2xl whitespace-pre-line">
                    {about.team_intro}
                  </p>
                </Reveal>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-20 md:gap-y-24">
            {visibleTeam.map((m, i) => (
              <Reveal key={i} delay={(i % 3) * 0.08}>
                <article data-testid={`team-member-${i}`} className="group">
                  {m.photo_url && (
                    <div className="mb-7">
                      <TeamPhoto3D src={m.photo_url} alt={m.name || ""} />
                    </div>
                  )}
                  {m.name && (
                    <h3 className="font-display text-2xl md:text-3xl tracking-tight">
                      {m.name}
                    </h3>
                  )}
                  {m.role && (
                    <p className="overline text-[#C9A050] mt-2">{m.role}</p>
                  )}
                  {m.bio && (
                    <p className="text-[#A1A1AA] text-base leading-relaxed mt-4 max-w-md whitespace-pre-line">
                      {m.bio}
                    </p>
                  )}
                </article>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* Section 6 — Soft CTA */}
      {about.cta_visible && about.cta_body && (
        <section data-testid="about-cta" className="px-6 md:px-14 lg:px-24 py-28 md:py-44">
          <div className="grid grid-cols-12">
            <div className="col-span-12 md:col-span-10 md:col-start-2">
              <Reveal>
                <p className="font-display text-3xl md:text-5xl lg:text-6xl leading-[1.15] tracking-[-0.02em] max-w-4xl whitespace-pre-line">
                  {about.cta_body}
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <a
                  href={ctaHref}
                  target={ctaHref.startsWith("http") ? "_blank" : undefined}
                  rel={ctaHref.startsWith("http") ? "noopener noreferrer" : undefined}
                  data-testid="about-cta-button"
                  data-cursor="Reach"
                  className="inline-flex items-center gap-3 mt-12 md:mt-16 overline border border-[#C9A050] text-[#C9A050] hover:bg-[#C9A050] hover:text-black px-7 py-4 transition-all"
                >
                  {about.cta_button_label || "Start a project"} →
                </a>
              </Reveal>
            </div>
          </div>
        </section>
      )}
      {/* Section 7 — Frequently asked (dynamic, edited in admin) */}
      {about.faq_visible && faqs.length > 0 && (
        <section data-testid="about-faq-section" className="px-6 md:px-14 lg:px-24 py-28 md:py-40 border-t border-white/5">
          <div className="grid grid-cols-12 gap-6 md:gap-10">
            <div className="col-span-12 md:col-span-4">
              <Reveal>
                <p className="overline text-[#C9A050]">{about.faq_label || "Frequently asked"}</p>
                {about.faq_heading && (
                  <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.1] tracking-[-0.02em] mt-6 max-w-md">
                    {about.faq_heading}
                  </h2>
                )}
              </Reveal>
            </div>
            <div className="col-span-12 md:col-span-8">
              <ul className="divide-y divide-white/10 border-t border-white/10">
                {faqs.map((f, i) => (
                  <FaqRow key={f.id || i} idx={i} q={f.question} a={f.answer} />
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}
    </main>
  );
}

function FaqRow({ idx, q, a }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <li className="py-6 md:py-7">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`about-faq-row-${idx}`}
        aria-expanded={open}
        className="w-full flex items-start justify-between gap-8 text-left group"
      >
        <span className="font-display text-xl md:text-2xl lg:text-3xl leading-tight tracking-tight text-white/95 group-hover:text-[#C9A050] transition-colors">
          {q}
        </span>
        <span
          aria-hidden="true"
          className={`shrink-0 mt-2 text-[#C9A050] transition-transform duration-300 ${open ? "rotate-45" : "rotate-0"}`}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4">
            <line x1="10" y1="3" x2="10" y2="17" />
            <line x1="3" y1="10" x2="17" y2="10" />
          </svg>
        </span>
      </button>
      <div
        className="overflow-hidden transition-all duration-500 ease-out"
        style={{ maxHeight: open ? 600 : 0, opacity: open ? 1 : 0 }}
      >
        <p className="text-sm md:text-base leading-relaxed text-white/70 mt-4 max-w-2xl whitespace-pre-line">
          {a}
        </p>
      </div>
    </li>
  );
}
